/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo, useState } from 'react';
import { Badge, Select } from '@crisellevaldez/speculo-ui';
import { Mail, MailOpen } from 'lucide-react';
import { OffsetPagination } from '../../../common/OffsetPagination';
import { EmptyStateWithIcon } from '../../../common/EmptyStateWithIcon';
import { formatDate } from '../../../../utils/dateTime';
import { useOrganizationStore } from '../../../../stores/useOrganizationStore';
import { ActivityTableHeader } from './ActivityTableHeader';
import { useContactEmailsGQL } from '../../../../hooks/graphql/contacts/useContactEmailsGQL';
import { isPreviewContactId, PREVIEW_EMAILS } from '../../../../pages/previewMockData';
import type { GQLMessageItem } from '../../../../hooks/graphql/contacts/useContact';
import { getEmailOutcomeStyle, formatEmailOutcome } from '../../../../utils/badgeStyles';
import { EmailPreviewModal } from './EmailPreviewModal';

interface EmailsTabProps {
  contactId: string;
  isActive?: boolean;
  /** Contact display name (e.g. "Jamie Chen") used in the email modal's
   *  To / From rows so the agent doesn't just see a raw email address. */
  contactName?: string;
  /** Contact email — fallback for the modal's To / From row when the
   *  message's own fromAddress/toAddress is empty. */
  contactEmail?: string;
}

const eventTypeOptions = [
  { value: '', label: 'All Events' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'opened', label: 'Opened' },
  { value: 'clicked', label: 'Clicked' },
];

const PREVIEW_LIMIT = 110;

/** Strip HTML (including <style>/<script> CSS so we don't leak template
 *  CSS into the preview), collapse whitespace, and truncate for the
 *  inbox row snippet. */
function bodyExcerpt(html: string | null | undefined, body: string | null | undefined): string {
  const raw = html || body || '';
  if (!raw) return '';
  let text = '';
  if (typeof window !== 'undefined' && html) {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    // Style / script content shows up in textContent — drop those nodes
    // so the preview is the actual visible email copy, not CSS.
    tmp.querySelectorAll('style, script, noscript').forEach((n) => n.remove());
    text = (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
  } else {
    text = raw
      .replace(/<style[\s\S]*?<\/style>/gi, '')
      .replace(/<script[\s\S]*?<\/script>/gi, '')
      .replace(/<[^>]+>/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }
  return text.length > PREVIEW_LIMIT ? text.slice(0, PREVIEW_LIMIT) + '…' : text;
}

interface EmailRowProps {
  email: GQLMessageItem;
  timezone?: string;
  senderName: string;
  senderEmail: string;
  onClick: () => void;
}

const EmailRow: React.FC<EmailRowProps> = ({
  email,
  timezone,
  senderName,
  senderEmail,
  onClick,
}) => {
  const isOpened = email.status === 'opened' || email.status === 'clicked' || !!email.openedAt;
  const sentAt = email.sentAt || email.deliveredAt || email.openedAt || email.createdAt;
  const preview = bodyExcerpt(email.bodyHtml, email.body);
  const statusStyle = email.status ? getEmailOutcomeStyle(email.status) : null;
  const statusLabel = email.status ? formatEmailOutcome(email.status) : null;

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left flex items-start gap-3 px-3 sm:px-4 py-3 border-b border-foreground/10 hover:bg-muted/40 transition-colors"
    >
      {/* Envelope icon — filled when opened, outline when not. */}
      <span className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300 flex-shrink-0">
        {isOpened ? <MailOpen className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
      </span>

      <div className="flex-1 min-w-0">
        {/* Row 1: SUBJECT (bold headline) + time + status on the right.
            Font sizes mirror the ActivityItem (Communication so far) so
            the two surfaces feel like the same row style. */}
        <div className="flex items-baseline justify-between gap-2 mb-0.5">
          <span className="text-[13px] font-semibold text-foreground tracking-tight truncate">
            {email.subject || '(no subject)'}
          </span>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {statusLabel && (
              <Badge
                className={`${statusStyle?.background ?? 'bg-muted text-foreground'} ${statusStyle?.text ?? ''}`}
              >
                {statusLabel}
              </Badge>
            )}
            <span className="text-[10px] xl:text-xs text-muted-foreground tabular-nums whitespace-nowrap uppercase">
              {sentAt && formatDate(sentAt, timezone, 'MMM D · h:mm A')}
            </span>
          </div>
        </div>

        {/* Row 2: SENDER name + <email> — matches the eAlert campaign From. */}
        <div className="text-xs text-foreground/85 truncate">
          <span className="font-medium">{senderName}</span>
          {senderEmail && (
            <span className="text-muted-foreground"> &lt;{senderEmail}&gt;</span>
          )}
        </div>

        {/* Row 3: body preview snippet (HTML/style/script stripped, muted,
            truncated). Sizes mirror ActivityDescriptionBlock. */}
        {preview && (
          <div className="text-xs xl:text-sm text-muted-foreground truncate mt-1">
            {preview}
          </div>
        )}
      </div>
    </button>
  );
};

export const EmailsTab: React.FC<EmailsTabProps> = ({ contactId, contactName, contactEmail }) => {
  const [dateRange, setDateRange] = useState<{ from: Date | null; to: Date | null }>({
    from: null,
    to: null,
  });
  const [selectedEventType, setSelectedEventType] = useState<string>('');
  const [selectedEmail, setSelectedEmail] = useState<GQLMessageItem | null>(null);

  const { organization } = useOrganizationStore();
  const timezone = organization?.timezone;
  // Sender display matches the eAlert campaign's "From" line — same fields
  // EmailCampaignsModal writes: sendgrid_reply_to_name + sendgrid_sender_email.
  const senderName =
    organization?.sendgrid_reply_to_name ||
    organization?.assistant_name ||
    organization?.org_name ||
    'Remi';
  const senderEmail =
    organization?.sendgrid_sender_email ||
    organization?.sendgrid_reply_to_email ||
    organization?.reply_to_email ||
    '';

  const isPreview = isPreviewContactId(contactId);
  const live = useContactEmailsGQL(
    isPreview ? '' : contactId,
    dateRange,
    selectedEventType ? [selectedEventType] : undefined,
    undefined,
    'created_at',
    'DESC',
  );
  const items = useMemo<GQLMessageItem[]>(
    () => (isPreview ? (PREVIEW_EMAILS as any) : live.items),
    [isPreview, live.items]
  );
  const total = isPreview ? PREVIEW_EMAILS.length : live.total;
  const loading = isPreview ? false : live.loading;
  const resetPage = live.resetPage;

  const handleDateRangeChange = (range: { from: Date | null; to: Date | null }) => {
    setDateRange(range);
    resetPage();
  };

  const handleFilterChange = (value: string | string[]) => {
    setSelectedEventType(Array.isArray(value) ? value[0] : value);
    resetPage();
  };

  return (
    <div className="flex flex-col h-full min-h-0 gap-4">
      <ActivityTableHeader
        title="Email History"
        dateRange={dateRange}
        onDateRangeChange={handleDateRangeChange}
        count={total}
        isLoading={loading}
      >
        <Select
          placeholder="Status"
          options={eventTypeOptions}
          value={selectedEventType}
          onChange={handleFilterChange}
          className="w-[120px] md:w-[160px]"
        />
      </ActivityTableHeader>

      {/* Inbox list — each row is a button that opens the email in a modal.
          Empty state renders plain (no card) to match the other tabs. */}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
          Loading emails…
        </div>
      ) : items.length === 0 ? (
        <EmptyStateWithIcon message="No emails yet" />
      ) : (
        <div className="flex-1 min-h-0 flex flex-col rounded-2xl ring-1 ring-foreground/10 bg-white dark:bg-card shadow-sm overflow-hidden">
          <div className="flex-1 min-h-0 overflow-y-auto scrollbar-custom">
            {items.map((email) => (
              <EmailRow
                key={email.messageId}
                email={email}
                timezone={timezone}
                senderName={senderName}
                senderEmail={senderEmail}
                onClick={() => setSelectedEmail(email)}
              />
            ))}
          </div>
          {!isPreview && total > 0 && (
            <OffsetPagination
              currentPage={live.currentPage}
              totalItems={total}
              pageSize={live.pageSize}
              onNextPage={live.handleNextPage}
              onPrevPage={live.handlePrevPage}
              onPageChange={live.goToPage}
              isLoading={loading}
              label="emails"
            />
          )}
        </div>
      )}

      <EmailPreviewModal
        isOpen={!!selectedEmail}
        onClose={() => setSelectedEmail(null)}
        email={selectedEmail}
        contactName={contactName}
        contactEmail={
          selectedEmail
            ? (selectedEmail.direction === 'inbound'
                ? selectedEmail.fromAddress
                : selectedEmail.toAddress) || contactEmail
            : undefined
        }
      />
    </div>
  );
};
